import type { LhcFilter, LhcItemsFilter } from '@diamond/shared/types';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { type Query, useQuery } from '@tanstack/react-query';

import {
  type CollectionBiltoDetailParams,
  getCollectionBiltoDetail,
  getCollectionPlants,
  getLHCDetail,
  getLHCList,
} from '../api';
import { COLLECTION_QUERY_KEYS } from '../api/constants';
import { captureLhcApiError } from '../utils/capture-lhc-api-error';

export function useLHCList(params: LhcFilter) {
  const session = useAuthStore();

  return useQuery({
    queryKey: [COLLECTION_QUERY_KEYS.LHC_LIST, params],
    queryFn: async () => {
      try {
        return await getLHCList(session.access_token, params);
      } catch (error: unknown) {
        captureLhcApiError(error, {
          action: 'getLhcList',
          extras: {
            filters: params as Record<string, unknown>,
          },
        });
        throw error;
      }
    },
    enabled: !!session.access_token,
  });
}

export function useCollectionPlants() {
  const session = useAuthStore();

  return useQuery({
    queryKey: [COLLECTION_QUERY_KEYS.COLLECTION_PLANTS],
    queryFn: async () => {
      try {
        return await getCollectionPlants(session.access_token);
      } catch (error: unknown) {
        captureLhcApiError(error, {
          action: 'getCollectionPlants',
        });
        throw error;
      }
    },
    enabled: !!session.access_token,
  });
}

export function useLHCDetail(id: string, params?: LhcItemsFilter) {
  const session = useAuthStore();

  return useQuery({
    queryKey: [COLLECTION_QUERY_KEYS.LHC_DETAIL, id, params],
    queryFn: async () => {
      try {
        return await getLHCDetail(session.access_token, id, params);
      } catch (error: unknown) {
        captureLhcApiError(error, {
          action: 'getLhcDetail',
          extras: {
            lhcId: id,
            filters: params as Record<string, unknown> | undefined,
          },
        });
        throw error;
      }
    },
    enabled: !!session.access_token && !!id,
  });
}

export function useCollectionBiltoDetail(
  sapNoBilto: string,
  params?: CollectionBiltoDetailParams
) {
  const session = useAuthStore();

  return useQuery({
    queryKey: [
      COLLECTION_QUERY_KEYS.COLLECTION_BILTO_DETAIL,
      sapNoBilto,
      params,
    ],
    queryFn: async () => {
      try {
        return await getCollectionBiltoDetail(
          session.access_token,
          sapNoBilto,
          params
        );
      } catch (error: unknown) {
        captureLhcApiError(error, {
          action: 'getCollectionBiltoDetail',
          extras: {
            sap_no_bilto: sapNoBilto,
            filters: params as Record<string, unknown> | undefined,
          },
        });
        throw error;
      }
    },
    enabled: !!session.access_token && !!sapNoBilto,
    placeholderData: (previousData, previousQuery: Query | undefined) => {
      const prevSap = previousQuery?.queryKey?.[1];
      if (typeof prevSap !== 'string' || prevSap !== sapNoBilto) {
        return undefined;
      }
      return previousData;
    },
  });
}
